import React, { useRef, useContext, useEffect, useState } from "react";
import { SimpleTooltip } from "simple-ui-components";

import BuildIcon from "@mui/icons-material/Build";
import AttachmentIcon from "@mui/icons-material/Attachment";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import CheckIcon from "@mui/icons-material/Check";

import { AuthContext } from "../authContextProvider";
import { Layout, useWindowSize } from "../layout/otherPage";
import {
  OuterWrapper,
  BodyWrapperStyled,
  BoxStyled,
  CardStyled,
  CardHeader,
  CardBody,
  CardFilter,
  EmptyList,
} from "../../theme";
import { get, del, post } from "../../server-requests/serverRequests";
import { LoadingList } from "../shared/loading";
import { RepairTypeDetail } from "./repairTypeDetail";
import { AddEditRepairTypes } from "./addEditRepairTypes";
import DeletePopup from "../shared/deletePopup";
import { FilterEquipmentByModel } from "./filterEquipmentByModel";
import { FilterRepairType } from "./filterRepairType";
import { getTranslation, LanguageContext } from "../languageProvider";
import { Log, ReplaceWord } from "../shared/utility";

const getIsActive = (list, id) => {
  if (list.length > 0) {
    return list.some((item) => item.modelRepairType.id === id);
  } else {
    return false;
  }
};

export const Repairs = () => {
  const addEditRepairTypesRef = useRef();
  const deleteRepairTypeRef = useRef();

  const { Tooltip } = SimpleTooltip;

  const { snackbarHandler } = useContext(AuthContext);
  const { lang } = useContext(LanguageContext);

  const { wrapperHeight, boxHeight } = useWindowSize();

  const [reloadRepairTypes, setReloadRepairTypesList] = useState(true);
  const [repairTypesList, setRepairTypesList] = useState([]);
  const [repairTypesListFull, setRepairTypesListFull] = useState([]);
  const [loadingRepairType, setLoadingRepairType] = useState(true);

  const [selectedModel, setSelectedModel] = useState("");

  const [filter, setFilter] = useState([]);
  const [filterRepairType, setFilterRepairType] = useState(false);

  // selectedRepairType
  const [selectedRepairType, setSelectedRepairType] = useState([]);
  const [reloadSelectedRepairType, setReloadSelectedRepairType] =
    useState(false);
  const [loadingSelectedRepairType, setLoadingSelectedRepairType] =
    useState(false);

  // get repair types list
  useEffect(() => {
    let isMounted = true;
    if (reloadRepairTypes) {
      get("models/repair/types/list")
        .then((repairType) => {
          if (isMounted) {
            setRepairTypesList(repairType);
            setRepairTypesListFull(repairType);
            setLoadingRepairType(false);
            setReloadRepairTypesList(false);
          }
        })
        .catch((status) => {
          setLoadingRepairType(false);
          setReloadRepairTypesList(false);
          snackbarHandler(getTranslation(`error${status}`));
          Log("Error: get models/repair/types/list" + status);
        });
    }
    return () => (isMounted = false);
  }, [reloadRepairTypes, snackbarHandler]);

  const addRepairTypeClickHandler = () =>
    addEditRepairTypesRef.current.RepairTypes("add");

  const editRepairTypeClickHandler = (repair) =>
    addEditRepairTypesRef.current.RepairTypes("edit", repair);

  const updateRepairTypeSuccessHandler = () => setReloadRepairTypesList(true);

  const addRepairTypeToEquipmentClickHandler = (repair) => {
    if (selectedModel) {
      post("models/repairs", {
        modelId: selectedModel.id,
        modelRepairTypeId: repair.id,
      })
        .then(() => {
          setReloadSelectedRepairType(true);
          setLoadingSelectedRepairType(true);
        })
        .catch((status) => {
          snackbarHandler(getTranslation(`error${status}`));
          Log("Error: update models/repairs" + status);
        });
    }
  };

  const deleteRepairTypeClickHandler = (id) =>
    deleteRepairTypeRef.current.Delete(
      id,
      `${getTranslation("0032")} ${
        getTranslation("0074") && getTranslation("0074").toLowerCase()
      }`,
      getTranslation("0120") &&
        ReplaceWord(
          getTranslation("0120"),
          "**NAME**",
          getTranslation("0074").toLowerCase()
        )
    );

  const deleteRepairTypesHandler = (id) => {
    del("models/repair/types", {
      id,
    })
      .then(() => {
        snackbarHandler(
          getTranslation("0121") &&
            ReplaceWord(
              getTranslation("0121"),
              "**NAME**",
              getTranslation("0074")
            ),
          "success"
        );
        setReloadRepairTypesList(true);
      })
      .catch((status) => {
        snackbarHandler(getTranslation(`error${status}`));
        Log("Error: delete models/repair/types" + status);
      });
  };

  const removeRepairTypeFromEquipmentClickHandler = (id) => {
    del("models/repairs", { id })
      .then(() => {
        setReloadSelectedRepairType(true);
        setLoadingSelectedRepairType(true);
      })
      .catch((status) => {
        snackbarHandler(getTranslation(`error${status}`));
        Log("Error: delete models/repairs" + status);
      });
  };

  const searchHandler = (model) => {
    setSelectedModel(model);
    setReloadSelectedRepairType(true);
    setLoadingSelectedRepairType(true);
  };

  const filterRouteSearchHandler = (value) => {
    setFilter(value);
    setFilterRepairType(true);
  };

  // Filter List
  useEffect(() => {
    if (filterRepairType) {
      let list = repairTypesListFull;
      if (filter) {
        if (filter !== "") {
          list = list.filter((repair) => {
            let repairName = repair.name ? repair.name.toUpperCase() : "";
            let repairDescription = repair.description
              ? repair.description.toUpperCase()
              : "";
            let items = repairName.includes(filter.toUpperCase());
            if (!items) {
              items = repairDescription.includes(filter.toUpperCase());
            }
            return items;
          });
        }
      }
      setRepairTypesList(list);
      setFilterRepairType(false);
    }
  }, [repairTypesListFull, filterRepairType, filter]);

  // get selected repair types list
  useEffect(() => {
    let isMounted = true;
    if (selectedModel && reloadSelectedRepairType) {
      get("models/repairs/list", {
        modelId: selectedModel.id,
      })
        .then((repairList) => {
          if (isMounted) {
            setSelectedRepairType(repairList);
            setReloadSelectedRepairType(false);
            setLoadingSelectedRepairType(false);
          }
        })
        .catch((status) => {
          snackbarHandler(getTranslation(`error${status}`));
          setReloadSelectedRepairType(false);
          setLoadingSelectedRepairType(false);
          Log("Error: get models/repairs/list" + status);
        });
    }
    return () => (isMounted = false);
  }, [selectedModel, reloadSelectedRepairType, snackbarHandler]);

  return (
    <Layout lang={lang}>
      <OuterWrapper height={wrapperHeight}>
        <BodyWrapperStyled fullHeight>
          <BoxStyled width="sm" col="3" noMargin>
            <CardStyled>
              <CardHeader>
                <CheckIcon className="mainIcon" />
                <div className="detail" data-translate="0150">
                  {getTranslation("0150")}
                  <span data-translate="0142">{getTranslation("0142")}</span>
                </div>
              </CardHeader>
              <CardBody height={boxHeight}>
                <FilterEquipmentByModel Selected={searchHandler} />
              </CardBody>
            </CardStyled>
          </BoxStyled>
          <BoxStyled width="sm" col="3" noMargin>
            <CardStyled>
              <CardHeader>
                <BuildIcon className="mainIcon" />
                <div className="detail" data-translate="0074">
                  {getTranslation("0074")}
                  <span data-translate="0074,0078">
                    {getTranslation("0078") &&
                      ReplaceWord(
                        getTranslation("0078"),
                        "**NUMBER**",
                        repairTypesList.length,
                        "**LISTNAME**",
                        getTranslation("0074").toLowerCase()
                      )}
                  </span>
                </div>
                <Tooltip
                  text={`${getTranslation("0087")} ${
                    getTranslation("0074") &&
                    getTranslation("0074").toLowerCase()
                  }`}
                  placement="left"
                >
                  <AddOutlinedIcon onClick={addRepairTypeClickHandler} />
                </Tooltip>
              </CardHeader>
              <CardFilter>
                <FilterRepairType Filter={filterRouteSearchHandler} />
              </CardFilter>
              <CardBody height={boxHeight} filter={64}>
                {loadingRepairType && <LoadingList count={5} />}
                {!loadingRepairType && repairTypesList.length === 0 && (
                  <EmptyList data-translate="0074,0123">
                    {getTranslation("0123") &&
                      ReplaceWord(
                        getTranslation("0123"),
                        "**NAME**",
                        getTranslation("0074").toLowerCase()
                      )}
                  </EmptyList>
                )}
                {!loadingRepairType &&
                  repairTypesList.length > 0 &&
                  repairTypesList.map((repairType, index) => (
                    <RepairTypeDetail
                      key={index}
                      repair={repairType}
                      Edit={editRepairTypeClickHandler}
                      Delete={deleteRepairTypeClickHandler}
                      Add={addRepairTypeToEquipmentClickHandler}
                      selectedModel={selectedModel ? true : false}
                      isActive={getIsActive(selectedRepairType, repairType.id)}
                    />
                  ))}
              </CardBody>
            </CardStyled>
          </BoxStyled>
          <BoxStyled width="sm" col="3" noMargin>
            <CardStyled>
              <CardHeader>
                <AttachmentIcon className="mainIcon" />
                <div className="detail">
                  {selectedModel && selectedModel.name}
                  <span data-translate="0144">
                    {getTranslation("0144") &&
                      ReplaceWord(
                        getTranslation("0144"),
                        "**NAME**",
                        selectedModel ? selectedModel.name : ""
                      )}
                  </span>
                </div>
              </CardHeader>
              <CardBody height={boxHeight}>
                {loadingSelectedRepairType && <LoadingList count={5} />}
                {!loadingSelectedRepairType &&
                  selectedRepairType.length === 0 && (
                    <EmptyList data-translate="0074,0123">
                      {getTranslation("0123") &&
                        ReplaceWord(
                          getTranslation("0123"),
                          "**NAME**",
                          getTranslation("0074").toLowerCase()
                        )}
                    </EmptyList>
                  )}
                {selectedRepairType.length > 0 &&
                  !loadingSelectedRepairType &&
                  selectedRepairType.map((repairType, index) => (
                    <RepairTypeDetail
                      key={index}
                      repair={repairType.modelRepairType}
                      repairTypeId={repairType.id}
                      Remove={removeRepairTypeFromEquipmentClickHandler}
                    />
                  ))}
              </CardBody>
            </CardStyled>
          </BoxStyled>
        </BodyWrapperStyled>
        <AddEditRepairTypes
          ref={addEditRepairTypesRef}
          UpdateSuccess={updateRepairTypeSuccessHandler}
        />
        <DeletePopup
          ref={deleteRepairTypeRef}
          DeleteSuccess={deleteRepairTypesHandler}
        />
      </OuterWrapper>
    </Layout>
  );
};
